import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  useConnectionOptions,
  useConnections,
  useDisconnectBot,
  useSelectNumber,
  useStartOnboarding,
} from "@/features/bots/hooks";
import { i18n } from "@/i18n";
import { getApiErrorMessage } from "@/lib/api";

interface Notice {
  kind: "error" | "info" | "success";
  text: string;
}

function waErrorMessage(waError: string | null): string {
  const key = waError ? `bots:onboarding.error.${waError}` : "bots:onboarding.error.generic";
  return i18n.exists(key) ? i18n.t(key) : i18n.t("bots:onboarding.error.generic");
}

function NoticeBanner({ notice, onDismiss }: { notice: Notice; onDismiss: () => void }) {
  const styles = {
    error: "bg-red-50 text-red-800 border-red-200",
    info: "bg-blue-50 text-blue-800 border-blue-200",
    success: "bg-green-50 text-green-800 border-green-200",
  } as const;
  return (
    <div className={`flex items-start justify-between gap-4 rounded border px-4 py-3 text-sm ${styles[notice.kind]}`}>
      <p>{notice.text}</p>
      <button type="button" onClick={onDismiss} className="text-xs underline">
        ✕
      </button>
    </div>
  );
}

function NumberSelection({
  botId,
  enabled,
  onSuccess,
}: {
  botId: string;
  enabled: boolean;
  onSuccess: () => void;
}) {
  const { t } = useTranslation(["bots"]);
  const { data: wabas, isLoading } = useConnectionOptions(botId, enabled);
  const selectNumber = useSelectNumber();
  const [wabaId, setWabaId] = useState("");
  const [phoneId, setPhoneId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const phones = useMemo(
    () => wabas?.find((w) => w.waba_id === wabaId)?.phone_numbers ?? [],
    [wabas, wabaId],
  );

  const handleConfirm = () => {
    setError(null);
    selectNumber.mutate(
      { botId, wabaId, phoneNumberId: phoneId },
      {
        onSuccess,
        onError: (err) => setError(getApiErrorMessage(err)),
      },
    );
  };

  if (isLoading) {
    return (
      <div className="card">
        <div className="card-body text-sm text-slate-500">{t("bots:onboarding.loadingOptions")}</div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-base font-semibold">{t("bots:onboarding.selectTitle")}</h2>
      </div>
      <div className="card-body space-y-4 text-sm">
        {!wabas?.length && <p>{t("bots:onboarding.noOptionsFound")}</p>}
        {wabas && wabas.length > 0 && (
          <>
            <label className="block space-y-1">
              <span className="font-medium">{t("bots:onboarding.chooseWabaLabel")}</span>
              <select
                className="input w-full"
                value={wabaId}
                onChange={(e) => {
                  setWabaId(e.target.value);
                  setPhoneId("");
                }}
              >
                <option value="">{t("bots:onboarding.selectWabaPlaceholder")}</option>
                {wabas.map((w) => (
                  <option key={w.waba_id} value={w.waba_id}>
                    {w.name || w.waba_id}
                  </option>
                ))}
              </select>
            </label>

            {wabaId && (
              <label className="block space-y-1">
                <span className="font-medium">{t("bots:onboarding.choosePhoneLabel")}</span>
                <select
                  className="input w-full"
                  value={phoneId}
                  onChange={(e) => setPhoneId(e.target.value)}
                >
                  <option value="">{t("bots:onboarding.selectPhonePlaceholder")}</option>
                  {phones.map((p) => (
                    <option key={p.phone_number_id} value={p.phone_number_id}>
                      {p.display_phone_number || p.phone_number_id}
                      {p.verified_name ? ` (${p.verified_name})` : ""}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {error && <p className="text-red-700">{error}</p>}

            <button
              type="button"
              className="btn-primary"
              disabled={!wabaId || !phoneId || selectNumber.isPending}
              onClick={handleConfirm}
            >
              {selectNumber.isPending
                ? t("bots:onboarding.confirming")
                : t("bots:onboarding.confirmButton")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function WhatsAppOnboardingPage() {
  const { t } = useTranslation(["bots", "common"]);
  const { botId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: connections, isLoading } = useConnections();
  const disconnect = useDisconnectBot();
  const startOnboarding = useStartOnboarding();

  const [notice, setNotice] = useState<Notice | null>(null);

  const connection = connections?.find((c) => c.bot_id === botId);
  const isPending = connection?.connection_status === "pending";
  // Once connected, this page only reports status: reconnecting goes through
  // Disconnect first.
  const isConnected = connection?.connection_status === "connected";
  const showConnectFlow = !isPending && !isConnected;

  // Consume the wa_status/wa_error params left by the backend's 302 once,
  // turning them into a dismissible banner. The active view derives from the
  // connection status itself, so a mid-flow refresh still works.
  const [paramsConsumed, setParamsConsumed] = useState(false);
  useEffect(() => {
    if (paramsConsumed) return;
    setParamsConsumed(true);
    const waStatus = searchParams.get("wa_status");
    const waError = searchParams.get("wa_error");
    if (!waStatus && !waError) return;
    if (waStatus === "connected") {
      setNotice({ kind: "success", text: t("bots:onboarding.connectedSuccess") });
    } else if (waStatus === "pending_selection") {
      setNotice({ kind: "info", text: t("bots:onboarding.pendingSelectionNotice") });
    } else {
      setNotice({ kind: "error", text: waErrorMessage(waError) });
    }
    setSearchParams({}, { replace: true });
  }, [paramsConsumed, searchParams, setSearchParams, t]);

  const handleConnect = () => {
    startOnboarding.mutate(botId, {
      onSuccess: (consentUrl) => {
        window.location.href = consentUrl;
      },
      onError: (err) => setNotice({ kind: "error", text: getApiErrorMessage(err) }),
    });
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <Link to={`/bots/${botId}`} className="text-sm text-brand-700 hover:underline">
        {t("bots:onboarding.backToBot")}
      </Link>
      <h1 className="text-2xl font-semibold">{t("bots:onboarding.title")}</h1>
      <p className="text-sm text-slate-500">{t("bots:onboarding.subtitle", { botId })}</p>

      {notice && <NoticeBanner notice={notice} onDismiss={() => setNotice(null)} />}

      {isPending && (
        <NumberSelection
          botId={botId}
          enabled={isPending}
          onSuccess={() => setNotice({ kind: "success", text: t("bots:onboarding.connectedSuccess") })}
        />
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="text-base font-semibold">{t("bots:onboarding.statusTitle")}</h2>
        </div>
        <div className="card-body text-sm text-slate-500">
          {isLoading && <p>{t("common:states.loading")}</p>}
          {!isLoading && !connection && <p>{t("bots:onboarding.noConnection")}</p>}
          {!isLoading && connection && (
            <div className="space-y-1">
              <p>
                {t("common:states.status")}{" "}
                <span className="font-medium text-slate-800">{connection.connection_status}</span>
              </p>
              {connection.phone_number && (
                <p>
                  {t("bots:onboarding.phoneLabel")} {connection.phone_number}
                </p>
              )}
              {connection.phone_number_id && (
                <p>
                  {t("bots:onboarding.phoneIdLabel")} {connection.phone_number_id}
                </p>
              )}
              {connection.waba_id && (
                <p>
                  {t("bots:onboarding.wabaLabel")} {connection.waba_id}
                </p>
              )}
              <p>
                {t("bots:detail.webhookLabel")} {connection.webhook_status}
              </p>
              <div className="pt-2">
                <button
                  type="button"
                  className="btn-danger"
                  disabled={disconnect.isPending}
                  onClick={() =>
                    window.confirm(t("bots:onboarding.disconnectConfirm")) &&
                    disconnect.mutate(botId)
                  }
                >
                  {disconnect.isPending
                    ? t("bots:onboarding.disconnecting")
                    : t("bots:onboarding.disconnect")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConnectFlow && (
        <>
          <ol className="card divide-y divide-slate-200 text-sm">
            <li className="px-6 py-4">
              <strong>1.</strong> {t("bots:onboarding.step1")}
            </li>
            <li className="px-6 py-4">
              <strong>2.</strong> {t("bots:onboarding.step2")}
            </li>
            <li className="px-6 py-4">
              <strong>3.</strong> {t("bots:onboarding.step3")}
            </li>
          </ol>

          <button
            type="button"
            className="btn-primary"
            disabled={startOnboarding.isPending || !botId}
            onClick={handleConnect}
          >
            {startOnboarding.isPending
              ? t("bots:onboarding.redirecting")
              : t("bots:onboarding.connectButton")}
          </button>
          <p className="text-xs text-slate-400">{t("bots:onboarding.coexistenceNote")}</p>
        </>
      )}
    </div>
  );
}
